import { checkDate, formatDate } from '../../../utilities/date-utils'
import { checkNumber, isValueEmpty } from '../../../utilities/math-utils'
import { equal } from './function-utils'

function addError(a: any, b: any) {
  return new Error(`Invalid arguments for add operator: ${a}, ${b}`)
}

function subtractError(a: any, b: any) {
  return new Error(`Invalid arguments for subtract operator: ${a}, ${b}`)
}

function multiplyError(a: any, b: any) {
  return new Error(`Invalid arguments for multiply operator: ${a}, ${b}`)
}

function divideError(a: any, b: any) {
  return new Error(`Invalid arguments for divide operator: ${a}, ${b}`)
}

function modError(a: any, b: any) {
  return new Error(`Invalid arguments for mod operator: ${a}, ${b}`)
}

// Shared logic for the relational operators (<, <=, >, >=). Matches V2 (apps/dg/formula/formula.js)
// in propagating empty values as "" and NaN values as NaN. Empty propagates against numeric or
// empty operands; an empty paired with a non-numeric string falls through to lexical comparison.
function compareValues(a: any, b: any, compare: (x: number | string, y: number | string) => boolean) {
  if (Number.isNaN(a) || Number.isNaN(b)) return NaN
  const isAEmpty = isValueEmpty(a)
  const isBEmpty = isValueEmpty(b)
  if (isAEmpty && isBEmpty) return ""
  // Fast path: both arguments are already numbers — skip expensive date parsing
  if (typeof a === "number" && typeof b === "number") return compare(a, b)
  const [isADate, aDate] = checkDate(a)
  const [isBDate, bDate] = checkDate(b)
  if (isADate) a = aDate.valueOf() / 1000
  if (isBDate) b = bDate.valueOf() / 1000
  // compare numerically if possible
  const [isANumber, aNumber] = checkNumber(a)
  const [isBNumber, bNumber] = checkNumber(b)
  if (isANumber && isBNumber) return compare(aNumber, bNumber)
  // empty paired with a numeric value → propagate empty (V2 parity)
  if ((isAEmpty && isBNumber) || (isANumber && isBEmpty)) return ""
  // compare as strings; normalize empty operands to "" so e.g. null compares lexically as ""
  // rather than as "null" (V2 falls through to String(...) and has the same edge case)
  return compare(isAEmpty ? "" : String(a), isBEmpty ? "" : String(b))
}

export const operators = {
  // equal(a, b) or a == b
  // Note that we need to override default MathJs implementation so we can compare strings like "ABC" == "CDE".
  // MathJs doesn't allow that by default, as it assumes that equal operator can be used only with numbers.
  equal: {
    isOperator: true,
    numOfRequiredArguments: 2,
    evaluateOperator: equal
  },

  unequal: {
    isOperator: true,
    numOfRequiredArguments: 2,
    evaluateOperator: (a: any, b: any) => !equal(a, b)
  },

  smaller: {
    isOperator: true,
    numOfRequiredArguments: 2,
    evaluateOperator: (a: any, b: any) => compareValues(a, b, (x, y) => x < y)
  },

  smallerEq: {
    isOperator: true,
    numOfRequiredArguments: 2,
    evaluateOperator: (a: any, b: any) => compareValues(a, b, (x, y) => x <= y)
  },

  larger: {
    isOperator: true,
    numOfRequiredArguments: 2,
    evaluateOperator: (a: any, b: any) => compareValues(a, b, (x, y) => x > y)
  },

  largerEq: {
    isOperator: true,
    numOfRequiredArguments: 2,
    evaluateOperator: (a: any, b: any) => compareValues(a, b, (x, y) => x >= y)
  },

  add: {
    isOperator: true,
    numOfRequiredArguments: 2,
    evaluateOperator: (a: any, b: any) => {
      // empty strings
      if (a === "" && typeof b !== "string") return ""
      if (b === "" && typeof a !== "string") return ""

      // Fast path: both arguments are already numbers — skip expensive date parsing
      if (typeof a === "number" && typeof b === "number") {
        return a + b
      }

      const [isADate, aDate] = checkDate(a)
      const [isBDate, bDate] = checkDate(b)
      const [isANumber, aNumber] = checkNumber(a)
      const [isBNumber, bNumber] = checkNumber(b)

      // both are dates
      if (isADate && isBDate) {
        throw addError(a, b)
      }
      // add a number in seconds to a date
      if (isADate && isBNumber) {
        return new Date(aDate.valueOf() + bNumber * 1000)
      }
      if (isANumber && isBDate) {
        return new Date(aNumber * 1000 + bDate.valueOf())
      }

      // concatenate a date with a string
      if (isADate && typeof b === "string") {
        return formatDate(aDate) + b
      }
      if (typeof a === "string" && isBDate) {
        return a + formatDate(bDate)
      }

      // Numbers
      if (isANumber && isBNumber) {
        return aNumber + bNumber
      }

      // Strings
      if (typeof a === "string" || typeof b === "string") {
        // Two non-date and non-numeric strings, so concatenate them.
        return a + b
      }

      /* istanbul ignore next */
      throw addError(a, b)
    }
  },

  subtract: {
    isOperator: true,
    numOfRequiredArguments: 2,
    evaluateOperator: (a: any, b: any) => {
      // Empty strings
      if (a === "" || b === "") {
        return ""
      }

      // Fast path: both arguments are already numbers — skip expensive date parsing
      if (typeof a === "number" && typeof b === "number") {
        return a - b
      }

      const [isADate, aDate] = checkDate(a)
      const [isBDate, bDate] = checkDate(b)
      const [isANumber, aNumber] = checkNumber(a)
      const [isBNumber, bNumber] = checkNumber(b)

      // Date objects
      if (isADate || isBDate) {
        // both are dates
        if (isADate && isBDate) {
          return (aDate.valueOf() - bDate.valueOf()) / 1000
        }
        // subtract seconds from a date
        if (isADate && isBNumber) {
          return new Date(aDate.valueOf() - bNumber * 1000)
        }
        // can't subtract a date from seconds, etc.
        throw subtractError(a, b)
      }

      // Numbers
      if (isANumber && isBNumber) {
        return aNumber - bNumber
      }

      throw subtractError(a, b)
    }
  },

  multiply: {
    isOperator: true,
    numOfRequiredArguments: 2,
    evaluateOperator: (a: any, b: any) => {
      if (a === "" || b === "") return ""
      // Fast path: both arguments are already numbers
      if (typeof a === "number" && typeof b === "number") return a * b

      const [isANumber, aNumber] = checkNumber(a)
      const [isBNumber, bNumber] = checkNumber(b)

      if (!isANumber || !isBNumber) {
        throw multiplyError(a, b)
      }

      return aNumber * bNumber
    }
  },

  divide: {
    isOperator: true,
    numOfRequiredArguments: 2,
    evaluateOperator: (a: any, b: any) => {
      if (a === "" || b === "") return ""
      // Fast path: both arguments are already numbers
      if (typeof a === "number" && typeof b === "number") return a / b

      const [isANumber, aNumber] = checkNumber(a)
      const [isBNumber, bNumber] = checkNumber(b)

      if (!isANumber || !isBNumber) {
        throw divideError(a, b)
      }

      return aNumber / bNumber
    }
  },

  mod: {
    isOperator: true,
    numOfRequiredArguments: 2,
    evaluateOperator: (a: any, b: any) => {
      if (a === "" || b === "") return ""
      // Fast path: both arguments are already numbers
      if (typeof a === "number" && typeof b === "number") return a % b

      const [isANumber, aNumber] = checkNumber(a)
      const [isBNumber, bNumber] = checkNumber(b)

      if (!isANumber || !isBNumber) {
        throw modError(a, b)
      }

      return aNumber % bNumber
    }
  }
}
